import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { AlertTriangle, Copy, ClipboardList, Mail, RefreshCw, Trash2, ChevronDown, ChevronUp } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";

/**
 * Session 47 — Platform admin → System error log.
 *
 * Every backend 500, device crash and business-logic anomaly (duplicate
 * orders, items added after payment, stock movements on voids, …) from
 * every business lands here. Each row is copy-pasteable JSON so the
 * admin can paste it straight into a support conversation.
 */
const API = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "") + "/api";
const http = axios.create({ baseURL: API });

const SEVERITY_STYLE = {
  critical: "border-destructive/50 bg-destructive/10 text-destructive",
  error: "border-orange-500/40 bg-orange-500/10 text-orange-700 dark:text-orange-300",
  warning: "border-amber-500/40 bg-amber-500/10 text-amber-700 dark:text-amber-300",
  info: "border-border bg-secondary/60 text-muted-foreground",
};

function entryText(e) {
  const clean = Object.fromEntries(Object.entries(e).filter(([k, v]) => k !== "dedupe_key" && v !== null && v !== undefined && !(typeof v === "object" && Object.keys(v).length === 0)));
  return JSON.stringify(clean, null, 2);
}

/**
 * Session 60 — one paste for a whole business: a short header (business,
 * window, count, breakdown by kind) followed by every entry as JSON.
 */
function businessReportText(businessName, entries) {
  const byKind = {};
  for (const e of entries) {
    const k = `${e.severity || "?"} · ${e.source || "?"} · ${e.kind || "?"}`;
    byKind[k] = (byKind[k] || 0) + (Number(e.repeat_count) || 1);
  }
  const first = entries.length ? entries[entries.length - 1].created_at : null;
  const last = entries.length ? entries[0].created_at : null;
  const header = [
    `SYSTEM ERROR LOG — ${businessName}`,
    `Entries: ${entries.length}${first ? ` · from ${new Date(first).toLocaleString()} to ${new Date(last).toLocaleString()}` : ""}`,
    `Copied: ${new Date().toLocaleString()}`,
    "",
    "Breakdown:",
    ...Object.entries(byKind).sort((a, b) => b[1] - a[1]).map(([k, n]) => `  ${n} × ${k}`),
    "",
    "────────────────────────────────────────",
    "",
  ];
  return header.join("\n") + entries.map(entryText).join("\n\n");
}

async function copyText(text, okMsg) {
  try {
    await navigator.clipboard.writeText(text);
    toast.success(okMsg || "Copied");
  } catch (_) {
    toast.error("Couldn't copy — select the text manually.");
  }
}

export const AdminErrorLogPanel = ({ token, businesses = [] }) => {
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);
  const [rows, setRows] = useState([]);
  const [counts, setCounts] = useState(null);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(false);
  const [severity, setSeverity] = useState("all");
  const [source, setSource] = useState("all");
  const [business, setBusiness] = useState("all");
  const [hours, setHours] = useState("24");
  const [open, setOpen] = useState({});
  const [emailDraft, setEmailDraft] = useState("");
  const [collapsed, setCollapsed] = useState(false);
  const [copyingBusiness, setCopyingBusiness] = useState(false);
  const selectedBusiness = useMemo(() => businesses.find((b) => b.id === business) || null, [businesses, business]);

  // Session 60 — copy EVERYTHING on record for the selected business
  // (all severities, all sources, all time), not just the rows shown.
  const copyBusinessLog = async () => {
    if (!selectedBusiness) return;
    setCopyingBusiness(true);
    try {
      const r = await http.get("/admin/system-errors", { headers, params: { business_id: selectedBusiness.id, limit: 1000 } });
      const entries = r.data?.errors || [];
      if (!entries.length) { toast.info(`No entries on record for ${selectedBusiness.name}.`); return; }
      await copyText(businessReportText(selectedBusiness.name, entries), `Copied ${entries.length} entries for ${selectedBusiness.name}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't fetch the business log.");
    } finally {
      setCopyingBusiness(false);
    }
  };

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const params = { limit: 300 };
      if (severity !== "all") params.severity = severity;
      if (source !== "all") params.source = source;
      if (business !== "all") params.business_id = business;
      if (hours !== "all") params.since_hours = Number(hours);
      const r = await http.get("/admin/system-errors", { headers, params });
      setRows(r.data?.errors || []);
      setCounts(r.data?.last_24h || null);
      setSettings(r.data?.settings || null);
      setEmailDraft((prev) => prev || r.data?.settings?.email || "");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't load the system error log.");
    } finally {
      setLoading(false);
    }
  }, [token, headers, severity, source, business, hours]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const id = window.setInterval(load, 60000);
    return () => window.clearInterval(id);
  }, [load]);

  const patchSettings = async (patch) => {
    try {
      const r = await http.patch("/admin/system-errors/settings", patch, { headers });
      setSettings(r.data);
      if (patch.email !== undefined) toast.success("Alert email saved.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save settings.");
    }
  };

  const sendTest = async () => {
    try {
      await http.post("/admin/system-errors/test", {}, { headers });
      toast.success(`Test alert sent to ${settings?.email || "the alert email"}.`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Test email failed.");
    }
  };

  const clearAll = async () => {
    if (!window.confirm("Clear the whole system error log?")) return;
    try {
      await http.delete("/admin/system-errors", { headers });
      setRows([]);
      toast.success("Error log cleared.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't clear the log.");
    }
  };

  const total24 = counts ? Object.values(counts).reduce((a, b) => a + b, 0) : 0;

  return (
    <Card className="rounded-[24px] border-border bg-card" data-testid="admin-error-log-panel">
      <CardHeader className="pb-3">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <CardTitle className="flex items-center gap-2 text-lg">
              <AlertTriangle className="h-5 w-5 text-amber-600" />
              System error log
              {counts ? (
                <span className="ml-2 flex flex-wrap gap-1">
                  {["critical", "error", "warning", "info"].map((s) => (
                    <Badge key={s} variant="outline" className={`rounded-md text-[10px] ${SEVERITY_STYLE[s]}`} data-testid={`admin-error-count-${s}`}>
                      {counts[s] || 0} {s}
                    </Badge>
                  ))}
                </span>
              ) : null}
            </CardTitle>
            <CardDescription>
              Real faults only: backend errors, device crashes, sync and settings-drift problems, and integrity anomalies (double orders, items added after payment, negative stock, unassigned tables) from every business — last 24 h: {total24}. Copy one entry, everything shown, or the whole log for one business.
            </CardDescription>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Button variant="outline" size="sm" className="rounded-xl" onClick={load} disabled={loading} data-testid="admin-error-log-refresh">
              <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} /> Refresh
            </Button>
            <Button variant="outline" size="sm" className="rounded-xl" onClick={() => copyText(rows.map(entryText).join("\n\n"), `Copied ${rows.length} entries`)} disabled={!rows.length} data-testid="admin-error-log-copy-all">
              <Copy className="h-4 w-4" /> Copy all shown
            </Button>
            <Button
              variant="outline" size="sm"
              className="rounded-xl border-amber-500/50 bg-amber-500/10 text-amber-800 hover:bg-amber-500/20 dark:text-amber-200"
              onClick={copyBusinessLog}
              disabled={!selectedBusiness || copyingBusiness}
              title={selectedBusiness ? `Copy every entry on record for ${selectedBusiness.name}` : "Pick a business in the filters below to copy its whole log"}
              data-testid="admin-error-log-copy-business"
            >
              <ClipboardList className="h-4 w-4" /> {copyingBusiness ? "Copying…" : selectedBusiness ? `Copy whole log · ${selectedBusiness.name}` : "Copy whole log for a business"}
            </Button>
            <Button variant="outline" size="sm" className="rounded-xl text-destructive hover:bg-destructive/10 hover:text-destructive" onClick={clearAll} disabled={!rows.length} data-testid="admin-error-log-clear">
              <Trash2 className="h-4 w-4" /> Clear
            </Button>
            <Button variant="ghost" size="sm" className="rounded-xl" onClick={() => setCollapsed((c) => !c)} data-testid="admin-error-log-toggle">
              {collapsed ? <ChevronDown className="h-4 w-4" /> : <ChevronUp className="h-4 w-4" />}
            </Button>
          </div>
        </div>
      </CardHeader>
      {collapsed ? null : (
        <CardContent className="space-y-4">
          {/* Email alerts */}
          <div className="grid gap-3 rounded-2xl border border-border bg-secondary/40 p-3 md:grid-cols-[1fr_auto_auto_auto] md:items-end" data-testid="admin-error-email-settings">
            <div className="space-y-1">
              <Label htmlFor="admin-error-email" className="text-xs">Email alerts to</Label>
              <div className="flex gap-2">
                <Input id="admin-error-email" type="email" value={emailDraft} onChange={(e) => setEmailDraft(e.target.value)} className="h-9 rounded-xl" data-testid="admin-error-email-input" />
                <Button size="sm" variant="outline" className="h-9 rounded-xl" onClick={() => patchSettings({ email: emailDraft.trim() })} disabled={!emailDraft.trim() || emailDraft.trim() === settings?.email} data-testid="admin-error-email-save">Save</Button>
              </div>
              {settings && !settings.email_configured ? <p className="text-[11px] text-destructive">Platform email (GMAIL_USER / GMAIL_APP_PASSWORD) is not configured — alerts can't be sent.</p> : null}
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Minimum severity</Label>
              <Select value={settings?.min_severity || "warning"} onValueChange={(v) => patchSettings({ min_severity: v })}>
                <SelectTrigger className="h-9 w-36 rounded-xl text-xs" data-testid="admin-error-min-severity"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="info">Info and up</SelectItem>
                  <SelectItem value="warning">Warning and up</SelectItem>
                  <SelectItem value="error">Error and up</SelectItem>
                  <SelectItem value="critical">Critical only</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <label className="flex h-9 items-center gap-2 text-xs">
              <Switch checked={settings?.email_enabled !== false} onCheckedChange={(v) => patchSettings({ email_enabled: Boolean(v) })} data-testid="admin-error-email-toggle" />
              Email alerts on
            </label>
            <Button size="sm" variant="outline" className="h-9 rounded-xl" onClick={sendTest} data-testid="admin-error-email-test">
              <Mail className="h-4 w-4" /> Send test
            </Button>
          </div>

          {/* Filters */}
          <div className="flex flex-wrap gap-2">
            <Select value={severity} onValueChange={setSeverity}>
              <SelectTrigger className="h-9 w-36 rounded-xl text-xs" data-testid="admin-error-filter-severity"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All severities</SelectItem>
                <SelectItem value="critical">Critical</SelectItem>
                <SelectItem value="error">Error</SelectItem>
                <SelectItem value="warning">Warning</SelectItem>
                <SelectItem value="info">Info</SelectItem>
              </SelectContent>
            </Select>
            <Select value={source} onValueChange={setSource}>
              <SelectTrigger className="h-9 w-36 rounded-xl text-xs" data-testid="admin-error-filter-source"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All sources</SelectItem>
                <SelectItem value="backend">Backend</SelectItem>
                <SelectItem value="frontend">Devices / apps</SelectItem>
                <SelectItem value="detector">Anomaly detectors</SelectItem>
                <SelectItem value="config_sync">Settings sync</SelectItem>
                <SelectItem value="sweeper">Health sweeper</SelectItem>
                <SelectItem value="admin">Admin</SelectItem>
              </SelectContent>
            </Select>
            <Select value={business} onValueChange={setBusiness}>
              <SelectTrigger className="h-9 w-48 rounded-xl text-xs" data-testid="admin-error-filter-business"><SelectValue placeholder="All businesses" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All businesses</SelectItem>
                {businesses.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={hours} onValueChange={setHours}>
              <SelectTrigger className="h-9 w-32 rounded-xl text-xs" data-testid="admin-error-filter-hours"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="1">Last hour</SelectItem>
                <SelectItem value="24">Last 24 h</SelectItem>
                <SelectItem value="168">Last 7 days</SelectItem>
                <SelectItem value="720">Last 30 days</SelectItem>
                <SelectItem value="all">Everything</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Entries */}
          {rows.length === 0 ? (
            <p className="rounded-2xl border border-dashed border-border p-6 text-center text-sm text-muted-foreground" data-testid="admin-error-log-empty">
              {loading ? "Loading…" : "No events match these filters."}
            </p>
          ) : (
            <div className="max-h-[560px] space-y-2 overflow-y-auto pr-1" data-testid="admin-error-log-list">
              {rows.map((e) => {
                const isOpen = !!open[e.id];
                return (
                  <div key={e.id} className={`rounded-2xl border p-3 ${SEVERITY_STYLE[e.severity] || SEVERITY_STYLE.info}`} data-testid={`admin-error-row-${e.id}`}>
                    <div className="flex flex-wrap items-start justify-between gap-2">
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-1.5 text-[11px] uppercase tracking-wide">
                          <span className="font-semibold">{e.severity}</span>
                          <span>·</span><span>{e.source}</span>
                          <span>·</span><span className="font-mono normal-case">{e.kind}</span>
                          {e.business_name ? <><span>·</span><span className="normal-case">{e.business_name}</span></> : null}
                          {Number(e.repeat_count) > 1 ? <Badge variant="outline" className="ml-1 rounded-md text-[10px]">×{e.repeat_count}</Badge> : null}
                          {e.emailed_at ? <Badge variant="outline" className="ml-1 rounded-md text-[10px]">emailed</Badge> : null}
                        </div>
                        <p className="mt-1 break-words text-sm text-foreground">{e.message}</p>
                        <p className="mt-0.5 text-[11px] text-muted-foreground">
                          {e.created_at ? new Date(e.created_at).toLocaleString() : ""}{e.route ? ` · ${e.route}` : ""}{e.actor ? ` · ${e.actor}` : ""}{e.device_id ? ` · device ${String(e.device_id).slice(0, 8)}` : ""}
                        </p>
                      </div>
                      <div className="flex shrink-0 gap-1">
                        <Button size="sm" variant="ghost" className="h-8 rounded-lg px-2 text-xs" onClick={() => copyText(entryText(e), "Entry copied")} data-testid={`admin-error-copy-${e.id}`}>
                          <Copy className="h-3.5 w-3.5" /> Copy
                        </Button>
                        <Button size="sm" variant="ghost" className="h-8 rounded-lg px-2 text-xs" onClick={() => setOpen((o) => ({ ...o, [e.id]: !isOpen }))} data-testid={`admin-error-details-${e.id}`}>
                          {isOpen ? "Hide" : "Details"}
                        </Button>
                      </div>
                    </div>
                    {isOpen ? (
                      <pre className="mt-2 max-h-72 overflow-auto rounded-xl bg-background/80 p-3 text-[11px] leading-relaxed text-foreground" data-testid={`admin-error-json-${e.id}`}>{entryText(e)}</pre>
                    ) : null}
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      )}
    </Card>
  );
};
